import {useAuth0} from '@auth0/auth0-react';
import {isFailedPaymentInstructionStatus} from '@sollapay/domain/rules';
import {
  PaymentInstructionStatus,
  PayoutInstructionStatus,
  RealtimeEventType
} from '@sollapay/enums';
import {formatNIS, formatDateTimeLocalized} from '@sollapay/utils';
import {useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {
  invalidatePaymentInstructions,
  invalidatePayoutInstructions,
  invalidateTrustFinancials
} from '@/features/trust-accounts/utils';
import {useRealtimeEvent} from '@/hooks/useRealtimeEvent';

import {
  showPaymentInstructionStatusToast,
  showPaymentReceivedToast,
  showPayoutCompletedToast,
  showPayoutInstructionStatusToast
} from '../components';

import type {PayoutInstructionStatusToastProps} from '../components';

/** Statuses worth interrupting the lawyer for — FUNDS_IN_TRANSIT displays as APPROVED in the UI, so it has no toast of its own. */
const PAYOUT_TOAST_STATUSES = new Set<PayoutInstructionStatus>([
  PayoutInstructionStatus.APPROVED,
  PayoutInstructionStatus.FAILED,
  PayoutInstructionStatus.CANCELLED
]);

export function usePaymentRealtimeUpdates(): void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const {i18n} = useTranslation('payments');
  const {user} = useAuth0();
  const currentUserId = user?.sub;

  useRealtimeEvent(RealtimeEventType.PaymentReceived, event => {
    invalidateTrustFinancials(queryClient, event.trustAccountId);
    invalidatePaymentInstructions(queryClient, event.trustAccountId);

    const amount = formatNIS(event.amount);
    const dateTime = formatDateTimeLocalized(event.transactionDate, i18n.language);

    showPaymentReceivedToast(event.senderName, amount, dateTime, () =>
      navigate(`/trust-accounts/${event.trustAccountId}/transactions`)
    );
  });

  useRealtimeEvent(RealtimeEventType.PaymentInstructionStatusChanged, event => {
    invalidatePaymentInstructions(queryClient, event.trustAccountId);
    // Payin status changes can open or void a ledger inflight, moving balance and transactions.
    invalidateTrustFinancials(queryClient, event.trustAccountId);

    const buyerPath = `/trust-accounts/${event.trustAccountId}/buyers/${event.buyerPartyId}`;

    if (
      event.status === PaymentInstructionStatus.AWAITING_FUNDS ||
      event.status === PaymentInstructionStatus.FUNDS_IN_TRANSIT
    ) {
      showPaymentInstructionStatusToast(event.status, () => navigate(buyerPath), {
        isManual: event.isManual
      });
    } else if (isFailedPaymentInstructionStatus(event.status)) {
      showPaymentInstructionStatusToast(
        event.status as PaymentInstructionStatus.FAILED | PaymentInstructionStatus.REJECTED,
        () => navigate(buyerPath),
        {amount: formatNIS(event.amount)}
      );
    }
  });

  useRealtimeEvent(RealtimeEventType.PayoutInstructionStatusChanged, event => {
    invalidatePayoutInstructions(queryClient, event.trustAccountId);

    // Whoever triggered this already saw an action toast — invalidation above still applies to them.
    if (event.actorUserId && event.actorUserId === currentUserId) return;

    if (PAYOUT_TOAST_STATUSES.has(event.status)) {
      showPayoutInstructionStatusToast(
        event.status as PayoutInstructionStatusToastProps['status'],
        formatNIS(event.amount),
        () => navigate(`/trust-accounts/${event.trustAccountId}/transactions`)
      );
    }
  });

  useRealtimeEvent(RealtimeEventType.PayoutCompleted, event => {
    invalidateTrustFinancials(queryClient, event.trustAccountId);

    const transactionsPath = `/trust-accounts/${event.trustAccountId}/transactions`;
    showPayoutCompletedToast(
      event.creditorName,
      formatNIS(event.amount),
      formatDateTimeLocalized(event.transactionDate, i18n.language),
      () => navigate(transactionsPath)
    );
  });
}
